import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { RxUpdate } from 'react-icons/rx';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useServiceActions from '../../../hooks/APIHooks/appointments/useServiceActions';
import { createEditServiceSchema } from '../../../schemas';
import { Service } from '../../../types/services';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaCheckboxField from '../Inputs/AcademiaCheckboxField';
import AcademiaColorPicker from '../Inputs/AcademiaColorPicker';
import AcademiaFileInputField from '../Inputs/AcademiaFileInputField';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const CreateEditServiceModal = ({
  title,
  service,
  handleModalClose,
}: {
  title: 'create' | 'update';
  service?: Service;
  handleModalClose: () => void;
}) => {
  // integration of API hooks here
  const { createNewServiceHandler, updateServiceHandler } =
    useServiceActions('');

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to create/update service declared here
  const createEditServiceHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      const body = new FormData();

      console.log('SERVICE BODY: ', values);

      // mandatory fields
      body.append('name', values.name);
      body.append('description', values.description);
      body.append('is_most_popular', values.is_popular);

      // optional fields
      values.background_color &&
        body.append('background_color', values.background_color);
      values.image && body.append('image', values.image);

      if (!service) {
        body.append('created_by', currentUser?.username as string);

        await createNewServiceHandler
          .mutateAsync({ body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Service Created!!');

              handleModalClose();
              queryClient.invalidateQueries({ queryKey: ['service-list'] });
            } else {
              toast.error(res.message || 'Service Creation Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      } else {
        body.append('updated_by', currentUser?.username as string);

        await updateServiceHandler
          .mutateAsync({ serviceId: service.id, body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Service Updated!!');

              handleModalClose();
              queryClient.invalidateQueries({ queryKey: ['service-list'] });
            } else {
              toast.error(res.message || 'Service Updating Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering create/edit service modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText={`${title} service`} />

      <Formik
        initialValues={{
          name: service?.name || '',
          description: service?.description || '',
          background_color: service?.background_color || '',
          image: '',
          is_popular: service?.is_most_popular || false,
        }}
        validationSchema={createEditServiceSchema}
        onSubmit={createEditServiceHandler}
      >
        {(props) => (
          <Form className='space-y-5'>
            <AcademiaInputField
              type='text'
              name='name'
              label='Service Name'
              placeholder='Enter service name here...'
              isRequired
            />

            <AcademiaTextAreaField
              name='description'
              label='Service Description'
              placeholder='Enter service description here...'
              isRequired
            />

            <AcademiaColorPicker
              name='background_color'
              placeholder='Pick Background Color...'
              label='Background Color'
            />

            <AcademiaFileInputField
              name='image'
              label='Service Image'
              btnText='Upload'
              accept='images/*'
              placeholder='Upload Service Image Here...'
              filePath={
                service?.image
                  ? `${process.env.REACT_APP_APPOINTMENT_SERVICE_PROD_API}${service.image}`
                  : ''
              }
            />

            <AcademiaCheckboxField name='is_popular' label='Mark As Popular?' />

            <AcademiaButton
              type='submit'
              variant='premium'
              icon={
                title === 'create' ? (
                  <TbSquareRoundedPlusFilled className='size-6' />
                ) : (
                  <RxUpdate className='size-6' />
                )
              }
              isLoading={props.isSubmitting}
              fullWidth
            >
              {title} Service
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default CreateEditServiceModal;
